<template>
  <div class="w-full flex" @mouseleave="closeSubmenu">
    <div class="w-1/2">
      <ul>
        <li
          v-for="(navItem, navItemIndex) in navItems"
          :key="navItem.id"
          :class="[
            'flex justify-between items-center p-2 rounded-md hover:bg-secondary',
            {
              'mt-2': navItemIndex !== 0,
              'bg-secondary': selectedNavItemId === navItem.id,
            },
          ]"
          @mouseover="openSubmenu(navItem.id)"
        >
          <div class="flex items-center">
            <div
              class="w-[84px] border-1 border-solid border-grey-600 rounded-md"
            >
              <div class="aspect-w-4 aspect-h-3">
                <picture v-if="navItem.transformedImages !== null">
                  <source
                    :data-srcset="navItem.transformedImages[0].url"
                    type="image/webp"
                  />
                  <source
                    :data-srcset="navItem.transformedImages[1].url"
                    type="image/jpeg"
                  />
                  <img
                    class="w-full h-full object-center object-cover"
                    :src="navItem.transformedImages[1].url"
                    :alt="navItem.transformedImages[1].title"
                  />
                </picture>
                <img
                  v-lazy
                  v-if="navItem.svgImage !== null"
                  :src="navItem.svgImage.url"
                  :alt="navItem.svgImage.title"
                  class="w-full h-full object-cover object-center"
                />
              </div>
            </div>
            <div class="px-4">
              <a
                v-if="navItem.url !== null"
                :href="navItem.url"
                :target="navItem.newWindow ? '_blank' : '_self'"
                :class="[
                  'u-body-md group relative inline-block text-primary-navy font-bold focus:outline-none',
                  navItem.classes,
                  {
                    'cursor-default': !navItem.url,
                  },
                ]"
              >
                <span
                  :class="[
                    'absolute -inset-1.5 border-2 border-solid border-grey-900 rounded-lg opacity-0 group-focus:opacity-100 transition-opacity duration-200 ease-in-out pointer-events-none',
                  ]"
                ></span>
                <span
                  :class="[
                    'group-hover:border-current border-b-2 border-solid border-transparent transition-colors duration-200 ease-in-out',
                  ]"
                >
                  {{ navItem.title }}
                </span>
              </a>

              <p
                :class="[
                  'u-body-md group relative inline-block text-primary-navy font-bold focus:outline-none',
                  navItem.classes,
                ]"
                v-else
              >
                {{ navItem.title }}
              </p>

              <p v-if="isCommunityType" class="u-body-sm">
                {{ navItem.children.length }} Communities
              </p>
            </div>
          </div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.17952 2.64098L11.359 7.8205L6.17952 13"
              stroke="#0B2537"
              stroke-width="2"
            />
          </svg>
        </li>
      </ul>
    </div>
    <div class="w-1/2">
      <transition
        appear
        :key="`megamenu-${selectedNavItemId}`"
        enter-class="menu-enter"
        enter-active-class="menu-enter-active"
        enter-to-class="menu-enter-to"
        leave-class="menu-leave"
        leave-active-class="menu-leave-active"
        leave-to-class="menu-leave-to"
      >
        <template
          v-if="selectedNavItem !== null && selectedNavItem.children.length > 0"
        >
          <div class="bg-secondary mx-6 pt-4 pb-8 px-6 rounded-lg">
            <p class="u-body font-bold mb-2">
              <template v-if="isCommunityType">Choose a community</template>
              <template v-else-if="isHomesType"
                >Narrow down your criteria</template
              >
            </p>
            <ul class="mb-3">
              <li
                v-for="(childItem, childItemIndex) in selectedNavItem.children"
                :key="childItem.id"
                :class="[
                  '',
                  {
                    'mt-3': childItemIndex !== 0,
                  },
                ]"
              >
                <a
                  :href="childItem.url"
                  :target="childItem.newWindow ? '_blank' : '_self'"
                  :class="[
                    'u-body-md group relative inline-block text-primary-navy focus:outline-none',
                    childItem.classes,
                    {
                      'cursor-default': !childItem.url,
                    },
                  ]"
                >
                  <span
                    :class="[
                      'absolute -inset-1.5 border-2 border-solid border-grey-900 rounded-lg opacity-0 group-focus:opacity-100 transition-opacity duration-200 ease-in-out pointer-events-none',
                    ]"
                  ></span>
                  <span
                    :class="[
                      'group-hover:border-current border-b-2 border-solid border-transparent transition-colors duration-200 ease-in-out',
                    ]"
                  >
                    {{ childItem.title }}
                  </span>

                  <span
                    v-if="childItem.tag !== null"
                    class="u-body-sm font-bold bg-white border-1 border-solid border-grey-700 py-1 px-2 rounded-md ml-2"
                    >{{ childItem.tag }}</span
                  >
                </a>
              </li>
            </ul>

            <a
              v-if="selectedNavItem.url !== null"
              :href="selectedNavItem.url"
              :target="selectedNavItem.newWindow ? '_blank' : '_self'"
              class="relative inline-flex flex-nowrap items-center font-circular font-bold text-sm leading-base group focus:outline-none"
            >
              <span
                class="absolute -inset-1.5 opacity-0 group-focus:opacity-100 border-2 border-grey-900 border-solid rounded-lg transition-opacity duration-200 ease-in-out pointer-events-none"
              ></span>
              <span
                class="text-grey-800 border-b-2 border-grey-800 border-solid transition duration-200 ease-in-out group-hover:border-transparent"
              >
                View all
              </span>
              <span class="w-6 h-6 ml-3">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  class="overflow-visible"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="12"
                    class="fill-current text-primary-200"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M14.6776 7.20438C14.287 6.81385 13.6539 6.81385 13.2634 7.20438C12.8728 7.5949 12.8728 8.22807 13.2634 8.61859L15.6448 11L6 11C5.44772 11 5 11.4477 5 12C5 12.5523 5.44772 13 6 13L15.5858 13L13.2634 15.3224C12.8728 15.713 12.8728 16.3461 13.2634 16.7367C13.6539 17.1272 14.287 17.1272 14.6776 16.7367L18.7366 12.6776C19.1271 12.2871 19.1271 11.6539 18.7366 11.2634L14.6776 7.20438Z"
                    class="fill-current text-grey-800 transition duration-200 ease-in-out transform group-hover:translate-x-3"
                  />
                </svg>
              </span>
            </a>
          </div>
        </template>
      </transition>
    </div>
  </div>
</template>

<script>
import VueLazyDirective from '../../../directives/v-lazy';
import { COMMUNITY_TYPE, HOMES_TYPE } from '../../../configs/type';

export default {
  directives: {
    lazy: VueLazyDirective,
  },
  props: {
    type: {
      type: String,
      required: true,
      validator(value) {
        return [COMMUNITY_TYPE, HOMES_TYPE].includes(value);
      },
    },
    navItems: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      selectedNavItemId: '',
    };
  },
  computed: {
    isCommunityType() {
      return this.type === COMMUNITY_TYPE;
    },
    isHomesType() {
      return this.type === HOMES_TYPE;
    },
    selectedNavItem() {
      if (this.selectedNavItemId === '') return null;
      const selectedNavItem = this.navItems.find(
        (navItem) => navItem.id === this.selectedNavItemId
      );
      if (selectedNavItem === undefined) return [];
      return selectedNavItem;
    },
  },
  methods: {
    openSubmenu(id) {
      if (this.selectedNavItemId !== id) {
        this.selectedNavItemId = '';

        this.$nextTick(() => {
          this.selectedNavItemId = id;
        });
      }
    },
    closeSubmenu() {
      this.selectedNavItemId = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.menu-enter-active {
  @apply transition-all duration-200 ease-in-out;
}
.menu-leave-active {
  @apply transition-all duration-200 ease-in-out;
}
.menu-enter,
.menu-leave-to {
  @apply -translate-x-8 opacity-0;
}
.menu-enter-to,
.menu-leave {
  @apply translate-x-0 opacity-100;
}
</style>
